import { useMemo, useCallback, useEffect } from 'react';
import { getTableViewColumns } from '../utils/getTableViewColumns';
import { TblCol } from '../table.types';
import { TblSectionSpec } from '../spec';
import { setCurrentTableView } from '../tableViewsRepo';
import { LegacyTableView } from '../table-view.types';

type UseTableViewSelectionParams = {
    tableId: string;
    tableViews: LegacyTableView[];
    currentViewId: string;
    setCurrentViewId: (id: string) => void;
    columnSpec: () => Map<string, TblCol>;
    defaultView: () => { sections?: TblSectionSpec[] };
    allColumnsView?: () => { sections?: TblSectionSpec[] };
    setTableState?: (fn: (draft: any) => void) => void; // Optional - for forcing state updates
};

type UseTableViewSelectionReturn = {
    viewFilteredColumns: TblCol[];
    viewFilteredSections: any[];
    currentView: LegacyTableView | null;
    handleViewChange: (viewId: string) => Promise<void>;
    allAvailableColumns: TblCol[];
    defaultSectionSpecs: TblSectionSpec[];
    allColumnsSectionSpecs: TblSectionSpec[];
};

/**
 * useTableViewSelection
 *
 * This hook provides filtered columns and sections for table rendering based on the current table view selection.
 * It is used by table components (e.g., FeedsListTable) to manage view-based column visibility and section layout.
 *
 * It supports both legacy table view formats (for persistence/state) and the UI filtering approach (using section specs).
 * The hook integrates with atom state for view selection and can force table reinitialization on view change.
 *
 * Usage:
 *   - Pass in tableViews (LegacyTableView[]), currentViewId, columnSpec, defaultView, and atom state.
 *   - Receives filtered columns/sections for rendering, and a handler for view changes.
 */
export const useTableViewSelection = ({
    tableId,
    tableViews,
    currentViewId,
    setCurrentViewId,
    columnSpec,
    defaultView,
    allColumnsView,
    setTableState,
}: UseTableViewSelectionParams): UseTableViewSelectionReturn => {
    // Get section specs for Default and All Columns
    const defaultSectionSpecs = useMemo(() => {
        const defaultViewSpec = defaultView();
        return defaultViewSpec.sections || [];
    }, [defaultView]);
    const allColumnsSectionSpecs = useMemo(() => {
        if (allColumnsView) {
            const allColumnsSpec = allColumnsView();
            return allColumnsSpec.sections || [];
        }
        return defaultSectionSpecs;
    }, [allColumnsView, defaultSectionSpecs]);

    // Get ALL available columns from the table spec
    const allAvailableColumns = useMemo(() => {
        const colSpec = columnSpec();
        return Array.from(colSpec.values());
    }, [columnSpec]);

    // Find the current view from the tableViews array
    const currentView = useMemo(() => {
        const foundView = tableViews.find(view => view.id === currentViewId) || null;
        return foundView;
    }, [tableViews, currentViewId]);

    // Log view changes for monitoring
    useEffect(() => {
        if (currentViewId && !currentView) {
            console.warn('⚠️ View ID exists but view not found:', currentViewId);
        }
    }, [currentViewId, currentView]);

    // Patch: If Default view, ignore currentView and use only originalSectionSpecs and allAvailableColumns
    const isDefaultView = currentViewId === '1' || currentViewId === 'default' || currentViewId === 'all';
    const sectionSpecs = isDefaultView ? defaultSectionSpecs : allColumnsSectionSpecs;
    const { columns: viewFilteredColumns, sections: viewFilteredSections } = getTableViewColumns({
        allColumns: allAvailableColumns,
        originalSectionSpecs: sectionSpecs,
        view: isDefaultView ? undefined : currentView || undefined,
    });

    // Handle view selection changes
    const handleViewChange = useCallback(
        async (viewId: string) => {
            try {
                // Update the current view in the UI immediately
                setCurrentViewId(viewId);

                // FORCE immediate table state update to ensure view change takes effect (if setter provided)
                if (setTableState) {
                    setTableState((draft: any) => {
                        draft.currentViewId = viewId;
                        draft.waiting = false;
                    });
                }

                // Save the current view selection to persistent storage
                await setCurrentTableView(tableId, viewId);
            } catch (error) {
                console.error('❌ Failed to save view selection:', error);
            }
        },
        [setCurrentViewId, setTableState, tableId],
    );

    return {
        viewFilteredColumns,
        viewFilteredSections,
        currentView,
        handleViewChange,
        allAvailableColumns,
        defaultSectionSpecs,
        allColumnsSectionSpecs,
    };
};
